import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import Tooltip from "../../tooltip";
import {
  calcularSemaforoPorHora,
  avaliarTemperatura,
  avaliarPrecipitacao,
  avaliarChuva,
  avaliarVento,
  avaliarUmidade,
} from "../../../utils/weatherTrafficLight";
import { Auxiliar, BodyP, TaglineG } from "../../../styles/stylesTypography";
import { theme } from "../../../styles/theme";
import * as S from "./styles";

/**
 * Semáforo Climático — tira horizontal com um "pastilha" colorida por horário
 * disponível na previsão, em sequência cronológica (sem grade dia x hora).
 *
 * Props:
 *   weatherData  {Object}  - Resposta bruta da OpenWeatherMap (/forecast), com .list
 *   fazendaId    {string|number}
 */
// Explicação de cada status, usada no tooltip de cada pastilha - o nome da cor
// sozinho ("green"/"yellow"/"red") não diz nada sobre o que ela significa.
const STATUS_LABELS = {
  green: "Tempo adequado para pulverizar.",
  yellow: "Tempo com atenção para pulverizar.",
  red: "Tempo não adequado para pulverizar.",
};

// Abreviação do dia da semana em minúsculas + ponto, igual ao calendário de
// referência ("qui.", "sex.", "sáb."...) - diferente do "Hoje"/"Amanhã" usado
// antes, que não fazia sentido numa tira contínua de vários dias.
const WEEKDAYS_ABBR = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

// Nome completo do dia da semana, usado só no tooltip (a tira usa a abreviação acima).
const WEEKDAYS_FULL = [
  "Domingo",
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
];

/** Monta o conteúdo do tooltip de uma pastilha: dia da semana por extenso,
 * data e hora daquele dado, o status geral ("Janela de pulverização") e cada
 * métrica (temperatura/precipitação/chance de chuva/vento/umidade) com a cor
 * do status INDIVIDUAL dela (avaliarX, ver utils/weatherTrafficLight.js) -
 * diferente do status geral, que é sempre o mais crítico entre os fatores.
 * Mesma estrutura de conteúdo (div + p, mesmo padding/maxWidth) usada pelos
 * outros Tippy do sistema - ver tooltipContentPhone em farm-header/index.js.
 * Texto escuro sobre fundo branco: tema próprio "weather" (ver
 * styles/global.js), cores invertidas em relação ao tema "light" padrão
 * (bolha escura/texto branco) usado no resto do sistema. */
const montarTooltip = (dateStr, hora, item, status) => {
  const [yyyy, mm, dd] = dateStr.split("-");
  const dateObj = new Date(parseInt(yyyy, 10), parseInt(mm, 10) - 1, parseInt(dd, 10));
  const diaSemanaCompleto = WEEKDAYS_FULL[dateObj.getDay()];
  const dataHora = `${dd}/${mm}/${yyyy}, ${hora}h`;

  // Mesma precisão dos gráficos de Temperatura/Vento/Umidade/Chuva (uma casa
  // decimal em temperatura e vento, inteiro no resto - ver forecastByHour em
  // GraphicPreview/index.js): os dois leem o mesmo item da previsão, então o
  // número tem que aparecer igual nos dois lugares. Arredondar pra inteiro aqui
  // fazia o mesmo horário mostrar "26°C" na tira e "25.9°C" no gráfico.
  const temp = item.main?.temp;
  const umidade = item.main?.humidity;
  const ventoKmh = item.wind?.speed != null ? Number((item.wind.speed * 3.6).toFixed(1)) : null;
  const chuvaPct = item.pop != null ? Math.round(item.pop * 100) : null;
  // Ausência do campo rain["3h"] = 0mm (sem chuva nesse intervalo), não dado faltando.
  const precipitacaoMm = item.rain?.["3h"] ?? 0;

  const metricas = [
    temp != null
      ? { label: "Temperatura", valor: `${Number(temp.toFixed(1))}°C`, status: avaliarTemperatura(temp).status }
      : null,
    { label: "Precipitação", valor: `${precipitacaoMm.toFixed(1)} mm`, status: avaliarPrecipitacao(precipitacaoMm).status },
    item.pop != null
      ? { label: "Probabilidade de chuva", valor: `${chuvaPct}%`, status: avaliarChuva(item.pop).status }
      : null,
    item.wind?.speed != null
      ? { label: "Velocidade do vento", valor: `${ventoKmh} km/h`, status: avaliarVento(item.wind.speed).status }
      : null,
    umidade != null
      ? { label: "Umidade", valor: `${umidade}%`, status: avaliarUmidade(umidade).status }
      : null,
  ].filter(Boolean);

  return (
    <div style={{ textAlign: "left", width: "230px" }}>
      {/* Tagline (11px, o tamanho mais próximo do catálogo) - #9aa0ad não existe
          no tema, Cinza_7 (#8A92A2) é o cinza mais próximo. */}
      <TaglineG
        color={theme.colors.gray.Cinza_7}
        fontWeight={400}
        style={{ margin: 0, textTransform: "none", letterSpacing: "normal" }}
      >
        {diaSemanaCompleto}
      </TaglineG>
      <BodyP color="#1F2937" fontWeight={700} style={{ margin: 0 }}>{dataHora}</BodyP>

      <S.TooltipStatusRow style={{ marginTop: "8px" }}>
        <S.TooltipBullet color={S.STATUS_COLORS[status] || S.STATUS_COLORS.empty} style={{ marginTop: "5px" }} />
        <Auxiliar color="#1F2937" style={{ margin: 0 }}>
          <b>Janela de pulverização:</b> {STATUS_LABELS[status] || status}
        </Auxiliar>
      </S.TooltipStatusRow>

      {metricas.length > 0 && (
        <>
          <S.TooltipDivider />
          <S.TooltipMetrics>
          {metricas.map((metrica) => (
            <S.TooltipMetricRow key={metrica.label}>
              <S.TooltipMetricLabel>
                <S.TooltipBullet color={S.STATUS_COLORS[metrica.status] || S.STATUS_COLORS.empty} />
                <Auxiliar color="#1F2937" style={{ margin: 0 }}>{metrica.label}</Auxiliar>
              </S.TooltipMetricLabel>
              <Auxiliar color="#1F2937" fontWeight={700} style={{ margin: 0 }}>{metrica.valor}</Auxiliar>
            </S.TooltipMetricRow>
          ))}
          </S.TooltipMetrics>
        </>
      )}
    </div>
  );
};

const WeatherTrafficLight = ({ weatherData, weatherError, fazendaId }) => {
  const history = useHistory();
  // No mobile (<769px) os rótulos de hora mudam: a cada 6h (00h/06h/12h/18h,
  // inclinados -45°) em vez de a cada 3h com o dia da semana na virada -
  // mesma mudança feita nos gráficos de Temperatura/Vento/Umidade/Chuva (ver
  // GraphicPreview/index.js), pra não ficar apertado/sobrepondo numa tela
  // estreita.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 769
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 769);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Lista plana (não mais grade dia x hora), em ordem cronológica - um item por
  // horário que a API efetivamente devolveu. Sem "buracos"/pastilhas vazias:
  // só mostra horário que a previsão realmente tem.
  const slots = useMemo(() => {
    if (!weatherData?.list?.length) return [];

    // `item.dt_txt`/`item.dt` vêm em UTC (documentado pela OpenWeatherMap); usar
    // `dt_txt` direto, como "hora já local", adiantava a tira em 3h (ex.: "15h"
    // mostrava a previsão das 12h). `item.dt` é o timestamp Unix (UTC) - formatar
    // ele já no fuso America/Sao_Paulo dá a data/hora local corretas, inclusive
    // quando a conversão muda o dia (ex.: 00h UTC vira 21h do dia anterior).
    const formatterData = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Sao_Paulo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const formatterHora = new Intl.DateTimeFormat("en-GB", {
      timeZone: "America/Sao_Paulo",
      hour: "2-digit",
      hour12: false,
    });

    const itens = weatherData.list
      .filter((item) => item.dt)
      .map((item) => {
        const dataHoraUtc = new Date(item.dt * 1000);
        const dateStr = formatterData.format(dataHoraUtc); // "YYYY-MM-DD" em America/Sao_Paulo
        const hour = parseInt(formatterHora.format(dataHoraUtc), 10); // 0-23 em America/Sao_Paulo
        return { item, dateStr, hour };
      });

    // Limitar a 5 dias (hoje + próximos 4) - mesmo corte usado nos cards/
    // gráficos diários (ver GraphicPreview), pra tira não mostrar um dia a
    // mais que o resto da tela.
    const diasToShow = 5;
    const allDates = [...new Set(itens.map((i) => i.dateStr))];
    const selectedDates = new Set(allDates.slice(0, diasToShow));

    return itens
      .filter(({ dateStr }) => selectedDates.has(dateStr))
      .map(({ item, dateStr, hour }) => {
        const [yyyy, mm, dd] = dateStr.split("-");
        const dateObj = new Date(parseInt(yyyy, 10), parseInt(mm, 10) - 1, parseInt(dd, 10));
        const diaSemana = WEEKDAYS_ABBR[dateObj.getDay()];

        // Rótulo do dia só na hora 0h - não no primeiro horário da sequência
        // daquele dia (que podia não ser 0h, ex.: hoje começando às 15h), senão
        // o nome do dia aparecia "flutuando" sozinho, sem número de hora junto
        // nem pastilha anterior pra se ancorar visualmente.
        const diaLabel = hour === 0 ? diaSemana : null;

        return {
          key: `${dateStr}-${hour}`,
          dateStr,
          hour,
          dia: parseInt(dd, 10),
          diaSemana,
          diaLabel,
          status: calcularSemaforoPorHora(item),
          item,
        };
      });
  }, [weatherData]);

  const handleVerDetalhes = () => {
    if (fazendaId) {
      history.push(`/previsao_tempo/${fazendaId}`);
    }
  };

  // Três estados distintos pra não confundir "ainda carregando" com "falhou"
  // ou com "carregou, mas não tem previsão útil pra essa localização" - os
  // três chegavam ao mesmo "Carregando..." antes, que nunca saía da tela em
  // caso de erro (nem avisava o usuário).
  if (weatherError) {
    return (
      <S.Container>
        <S.LoadingWrapper>
          <p>Não foi possível carregar a previsão do tempo. Tente novamente mais tarde.</p>
        </S.LoadingWrapper>
      </S.Container>
    );
  }

  if (!weatherData) {
    return (
      <S.Container>
        <S.LoadingWrapper>
          <p>Carregando semáforo climático...</p>
        </S.LoadingWrapper>
      </S.Container>
    );
  }

  if (!slots.length) {
    return (
      <S.Container>
        <S.LoadingWrapper>
          <p>Dados insuficientes para calcular o semáforo climático desta localização.</p>
        </S.LoadingWrapper>
      </S.Container>
    );
  }

  return (
    <S.Container>
      {/* Cabeçalho */}
      <S.Header>
        <S.HeaderLeft>
          <BodyP color={theme.colors.gray.Cinza_2} fontWeight={800}>
            Janela de pulverização
          </BodyP>
        </S.HeaderLeft>
      </S.Header>

      {/* Tira de pastilhas */}
      <S.StripWrapper>
        <S.Strip>
          <S.ChipsRow>
            {slots.map((slot) => (
              <S.ChipSlot key={slot.key}>
                <Tooltip
                  content={montarTooltip(slot.dateStr, slot.hour, slot.item, slot.status)}
                  theme="tooltip"
                >
                  <S.Chip status={slot.status} />
                </Tooltip>
              </S.ChipSlot>
            ))}
          </S.ChipsRow>

          {/* Linha de eixo ligando as horas/dias - contínua de ponta a ponta,
              por isso fica numa fileira própria (ver comentário em styles.js). */}
          <S.AxisLine />

          <S.LabelsRow>
            {slots.map((slot) => (
              <S.ChipSlot key={slot.key}>
                <S.Tick />
                {/* diaLabel só existe quando a hora é 0h (ver slots acima) - mostrar
                    "seg. 0" repete a informação à toa; o nome do dia substitui o "0"
                    em vez de aparecer do lado dele. */}
                <S.HourWrap>
                  <S.ChipLabelHour>
                    {isMobile
                      ? (slot.hour % 6 === 0 ? `${String(slot.hour).padStart(2, "0")}h` : "")
                      : (slot.diaLabel || slot.hour)}
                  </S.ChipLabelHour>
                </S.HourWrap>
              </S.ChipSlot>
            ))}
          </S.LabelsRow>
        </S.Strip>
      </S.StripWrapper>
    </S.Container>
  );
};

export default WeatherTrafficLight;
